import React from "react";
import { Chip, Tooltip, Box, Typography } from "@mui/material";
import { AccessTime, CheckCircle, Pending } from "@mui/icons-material";
import { CurrentWithdrawalRequestStatus } from "../services/purpose-service";

interface WithdrawalStatusChipProps {
  withdrawalRequest: CurrentWithdrawalRequestStatus;
  onViewDetails?: () => void;
}

const WithdrawalStatusChip: React.FC<WithdrawalStatusChipProps> = ({ withdrawalRequest, onViewDetails }) => {
  const getStatusConfig = () => {
    switch (withdrawalRequest.status) {
      case "PENDING":
        return {
          label: "Withdrawal Pending",
          color: "#ff9800" as const, // Orange
          backgroundColor: "#fff3e0",
          icon: <Pending sx={{ fontSize: 16 }} />,
        };
      case "IN_PROGRESS":
        return {
          label: "Processing",
          color: "#2196f3" as const, // Blue
          backgroundColor: "#e3f2fd",
          icon: <CheckCircle sx={{ fontSize: 16 }} />,
        };
      default:
        return {
          label: "Unknown",
          color: "#757575" as const, // Gray
          backgroundColor: "#f5f5f5",
          icon: <AccessTime sx={{ fontSize: 16 }} />,
        };
    }
  };

  const calculateDaysRemaining = () => {
    const now = new Date();
    const completionDate = new Date(withdrawalRequest.estimatedCompletionDate);
    const diffTime = completionDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const formatCompletionDate = () => {
    const date = new Date(withdrawalRequest.estimatedCompletionDate);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const statusConfig = getStatusConfig();
  const daysRemaining = calculateDaysRemaining();

  const tooltipContent = (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        Withdrawal Request #{withdrawalRequest.requestId}
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Status:</strong> {statusConfig.label}
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Submitted:</strong> {new Date(withdrawalRequest.submittedDate).toLocaleDateString()}
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Estimated Completion:</strong> {formatCompletionDate()}
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Days Remaining:</strong> {daysRemaining} day{daysRemaining !== 1 ? "s" : ""}
      </Typography>
      {withdrawalRequest.reason && (
        <Typography variant="body2">
          <strong>Reason:</strong> {withdrawalRequest.reason}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <Chip
        icon={statusConfig.icon}
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <span>{statusConfig.label}</span>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.7rem",
                opacity: 0.8,
                ml: 0.5,
              }}
            >
              ({daysRemaining}d left)
            </Typography>
          </Box>
        }
        onClick={onViewDetails}
        sx={{
          backgroundColor: statusConfig.backgroundColor,
          color: statusConfig.color,
          border: `1px solid ${statusConfig.color}`,
          fontWeight: 500,
          cursor: onViewDetails ? "pointer" : "default",
          "&:hover": onViewDetails
            ? {
                backgroundColor: statusConfig.color,
                color: "white",
                "& .MuiChip-icon": {
                  color: "white",
                },
              }
            : {},
          "& .MuiChip-icon": {
            color: statusConfig.color,
          },
        }}
      />
    </Tooltip>
  );
};

export default WithdrawalStatusChip;
